import {
  Autocomplete,
  Box,
  CircularProgress,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Proptypes from 'prop-types';
import { useMemo, useState } from 'react';

import { api as stockApi } from '../../store/domains/stock';
import IccidOrganizationFileUpload from '../common/IccidOrganizationFileUpload';
import IccidOrganizationRangeForm from '../common/IccidOrganizationRangeForm';
import IccidOrganizationListForm from '../common/IccidOrganizationListForm';
import IccidHolderChangeReport from '../common/IccidHolderChangeReport';
import Render from '../Render';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const TITLES = {
  assign: 'Assign ICCIDs to organization',
  unassign: 'Unassign ICCIDs from organization',
  transfer: 'Transfer ICCIDs between organizations',
};

function IccidOrganizationDialog({ open, handleClose, action = 'assign' }) {
  const [value, setValue] = useState(0);
  const [hubId, setHubId] = useState('');
  const [fromOrganization, setFromOrganization] = useState(null);
  const [toOrganization, setToOrganization] = useState(null);
  const [report, setReport] = useState(null);

  const isTransfer = action === 'transfer';
  const isUnassign = action === 'unassign';

  const { data: hubList, isLoading: hubListLoading } = stockApi.useGetHubList();
  const { data: hubOrganizations } = stockApi.useGetPosManagerOrganizations(hubId, { skip: !hubId });
  const { data: allOrganizations } = stockApi.useGetOrganizations();
  const [holderChangeMutation, { isLoading }] = stockApi.usePostIccidHolderChange();

  const organizationNames = useMemo(
    () => Object.fromEntries((allOrganizations || []).map(({ id, title }) => [id, title])),
    [allOrganizations]
  );

  const sameOrganization = isTransfer && fromOrganization && toOrganization && fromOrganization.id === toOrganization.id;
  const organizationsReady =
    isUnassign || (isTransfer ? fromOrganization && toOrganization && !sameOrganization : toOrganization);

  const handleChangeHub = (event) => {
    setHubId(event.target.value);
    setFromOrganization(null);
    if (!isTransfer) setToOrganization(null);
  };

  const resetState = () => {
    setValue(0);
    setHubId('');
    setFromOrganization(null);
    setToOrganization(null);
    setReport(null);
  };

  const handleSubmit = (door, payload) => {
    const organizationFields = {
      ...(!isUnassign && { toOrganizationId: toOrganization.id }),
      ...(isTransfer && { fromOrganizationId: fromOrganization.id }),
    };

    let data = { ...payload, ...organizationFields };
    if (door === 'file') {
      data = new FormData();
      data.append('file', payload);
      Object.entries(organizationFields).forEach(([key, id]) => data.append(key, String(id)));
    }

    holderChangeMutation({ door, action, data })
      .unwrap()
      .then(setReport)
      .catch(() => {});
  };

  if (hubListLoading) {
    return <CircularProgress />;
  }
  return (
    <Dialog maxWidth="md" open={open} onClose={handleClose} TransitionProps={{ onExited: resetState }} fullWidth>
      <DialogTitle>{TITLES[action]}</DialogTitle>
      <DialogContent>
        {report ? (
          <IccidHolderChangeReport report={report} organizationNames={organizationNames} onDone={handleClose} />
        ) : (
          <>
            <Render shouldRender={!isUnassign}>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel id="hub-label">Hub</InputLabel>
                  <Select labelId="hub-label" id="hub" label="Hub" value={hubId} onChange={handleChangeHub}>
                    {hubList?.map(({ hubId, title }) => (
                      <MenuItem key={hubId} value={hubId}>
                        {title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Render shouldRender={isTransfer}>
                  <Autocomplete
                    value={fromOrganization}
                    onChange={(_, newValue) => setFromOrganization(newValue)}
                    options={hubOrganizations || []}
                    isOptionEqualToValue={(option, selected) => option.id === selected.id}
                    disabled={!hubId}
                    renderInput={(params) => <TextField {...params} label="From organization" />}
                    fullWidth
                  />
                </Render>
                <Autocomplete
                  value={toOrganization}
                  onChange={(_, newValue) => setToOrganization(newValue)}
                  options={(isTransfer ? allOrganizations : hubOrganizations) || []}
                  isOptionEqualToValue={(option, selected) => option.id === selected.id}
                  disabled={!isTransfer && !hubId}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={isTransfer ? 'To organization' : 'Organization'}
                      error={!!sameOrganization}
                      helperText={sameOrganization ? 'Source and destination must differ' : null}
                    />
                  )}
                  fullWidth
                />
              </Stack>
            </Render>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={value} onChange={(_, newValue) => setValue(newValue)} aria-label="ICCID input type">
                <Tab label="Range" {...a11yProps(0)} />
                <Tab label="File" {...a11yProps(1)} />
                <Tab label="Text" {...a11yProps(2)} />
              </Tabs>
            </Box>
            <TabPanel value={value} index={0}>
              <IccidOrganizationRangeForm onSubmit={handleSubmit} loading={isLoading} disabled={!organizationsReady} />
            </TabPanel>
            <TabPanel value={value} index={1}>
              <IccidOrganizationFileUpload onSubmit={handleSubmit} loading={isLoading} disabled={!organizationsReady} />
            </TabPanel>
            <TabPanel value={value} index={2}>
              <IccidOrganizationListForm onSubmit={handleSubmit} loading={isLoading} disabled={!organizationsReady} />
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

IccidOrganizationDialog.propTypes = {
  open: Proptypes.bool,
  handleClose: Proptypes.func,
  action: Proptypes.oneOf(['assign', 'unassign', 'transfer']),
};

TabPanel.propTypes = {
  children: Proptypes.node,
  value: Proptypes.number,
  index: Proptypes.number,
};

export default IccidOrganizationDialog;
